import { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { refundService } from '../services';
import useBottomNavPadding from '../hooks/useBottomNavPadding';
import PageHeader from '../components/ui/PageHeader';
import { useTranslation } from 'react-i18next';

interface RefundApplication {
  id: string;
  orderId: string;
  reason: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
}

export default function RefundHistory() {
  const [applications, setApplications] = useState<RefundApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    const fetchRefundApplications = async () => {
      try {
        setLoading(true);
        const data = await refundService.getRefundApplications();
        setApplications(data);
        setError('');
      } catch (err) {
        console.error('Error fetching refund applications:', err);
        setError('Failed to load refund history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchRefundApplications();
  }, []);

  // Format date to a more readable format
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get status badge color
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Apply bottom padding for the navigation
  useBottomNavPadding();

  return (
    <main className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader title={t('refundHistory.pageTitle')} backPath="/me" />

      {/* Main Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
            <p>{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="ml-auto text-sm text-blue-600 hover:text-blue-800"
            >
              {t('refundHistory.retry')}
            </button>
          </div>
        ) : applications.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-600">{t('refundHistory.noRecords')}</p>
            <Link to="/subscriptions" className="mt-4 inline-block text-sm text-blue-600 hover:text-blue-800">
              {t('refundHistory.viewSubscriptions')}
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((application) => (
              <div 
                key={application.id} 
                className="bg-white rounded-lg shadow p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium">{t('refundHistory.orderPrefix')}{application.orderId}</p>
                    <p className="text-sm text-gray-600">
                      {t('refundHistory.submittedLabel')}{formatDate(application.createdAt)}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(application.status)}`}>
                    {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                  </span>
                </div>
                <div className="border-t border-gray-100 my-2 pt-2">
                  <p className="text-sm text-gray-700 mb-1">
                    <span className="font-medium">{t('refundHistory.amountLabel')}</span> {formatCurrency(application.amount)}
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">{t('refundHistory.reasonLabel')}</span> {application.reason}
                  </p>
                </div>
                {application.status === 'pending' && (
                  <div className="mt-3 text-xs text-gray-500">
                    {t('refundHistory.pendingMessage')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
} 